import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import GridViewIcon from "@mui/icons-material/GridView";
import ViewListIcon from "@mui/icons-material/ViewList";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EntityHubLayout from "../../components/product-manager/EntityHubLayout";
import EntityAssetCard from "../../components/product-manager/EntityAssetCard";
import ApiHubListView from "../../components/product-manager/ApiHubListView";
import EditApiDialog from "../../components/product-manager/EditApiDialog";
import BulkUploadApiSpecsDialog from "../../components/BulkUploadApiSpecsDialog";
import { formatApiDisplayName } from "../../utils/apiDisplayName";
import {
  filterByHubState,
  useBookmarkableHubState,
} from "../../hooks/useBookmarkableHubState";
import { apiService } from "../../api/services/apiService";
import type { API } from "../../types";
import { EntityStatus } from "../../types";
import { pmHubAddPath } from "../../utils/pmHubRoutes";
import {
  HUB_PAGE_SIZE_OPTIONS,
  hubTotalPages,
  paginateHubItems,
} from "../../utils/hubPagination";

type ViewMode = "card" | "list";

const ApiHubPage: React.FC = () => {
  const hubState = useBookmarkableHubState({
    extraParamKeys: ["version", "pageSize"],
  });
  const { editUuid, setEditUuid, version, setVersion, pageSize, setPageSize } =
    hubState;
  const [apis, setApis] = useState<API[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [toggleLoadingId, setToggleLoadingId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>("card");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [singleDeleteApi, setSingleDeleteApi] = useState<API | null>(null);

  const fetchApis = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setApis(await apiService.getApis());
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to fetch APIs");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchApis();
  }, [fetchApis]);

  const filtered = useMemo(() => {
    let list = filterByHubState(
      apis,
      hubState,
      undefined,
      (api) => [
        api.name,
        formatApiDisplayName(api.name),
        api.description ?? "",
        api.version ?? "",
      ],
    );
    if (hubState.version.trim()) {
      const v = hubState.version.toLowerCase();
      list = list.filter((a) =>
        (a.version ?? "").toLowerCase().includes(v),
      );
    }
    return list;
  }, [apis, hubState]);

  const paginated = useMemo(
    () => paginateHubItems(filtered, hubState.page, pageSize),
    [filtered, hubState.page, pageSize],
  );

  const pageIds = useMemo(
    () =>
      paginated.map((a) => a.uuid).filter((id): id is string => Boolean(id)),
    [paginated],
  );

  const allPageSelected =
    pageIds.length > 0 && pageIds.every((id) => selectedIds.has(id));
  const somePageSelected = pageIds.some((id) => selectedIds.has(id));

  useEffect(() => {
    setSelectedIds(new Set());
  }, [
    hubState.page,
    hubState.debouncedQ,
    hubState.statuses,
    hubState.version,
    pageSize,
  ]);

  useEffect(() => {
    const total = hubTotalPages(filtered.length, pageSize);
    if (hubState.page > total) {
      hubState.setPage(total);
    }
  }, [filtered.length, pageSize, hubState.page, hubState.setPage]);

  const editApi = useMemo(
    () => apis.find((a) => a.uuid === editUuid) ?? null,
    [apis, editUuid],
  );

  useEffect(() => {
    if (!editUuid || loading) return;
    if (!apis.some((a) => a.uuid === editUuid)) {
      setEditUuid(null);
    }
  }, [apis, editUuid, loading, setEditUuid]);

  const toggleSelect = useCallback((uuid: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(uuid)) next.delete(uuid);
      else next.add(uuid);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allPageSelected) {
        pageIds.forEach((id) => next.delete(id));
      } else {
        pageIds.forEach((id) => next.add(id));
      }
      return next;
    });
  }, [allPageSelected, pageIds]);

  const clearSelection = () => setSelectedIds(new Set());

  const buildUpdateInput = (api: API, status: EntityStatus) => ({
    name: api.name,
    version: api.version,
    status,
    description: api.description ?? undefined,
    specContent: api.specContent ?? undefined,
  });

  const handleToggle = async (api: API) => {
    if (!api.uuid) return;
    setToggleLoadingId(api.uuid);
    const newStatus =
      api.status === EntityStatus.ACTIVE
        ? EntityStatus.INACTIVE
        : EntityStatus.ACTIVE;
    try {
      const updated = await apiService.updateApi(
        api.uuid,
        buildUpdateInput(api, newStatus),
      );
      setApis((prev) =>
        prev.map((a) => (a.uuid === updated.uuid ? updated : a)),
      );
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : "Failed to update status");
    } finally {
      setToggleLoadingId(null);
    }
  };

  const removeApisFromState = (uuids: string[]) => {
    const removed = new Set(uuids);
    setApis((prev) => prev.filter((a) => !a.uuid || !removed.has(a.uuid)));
    setSelectedIds((prev) => {
      const next = new Set(prev);
      uuids.forEach((id) => next.delete(id));
      return next;
    });
    if (editUuid && removed.has(editUuid)) {
      setEditUuid(null);
    }
  };

  const handleDeleteOne = async (api: API) => {
    if (!api.uuid) return;
    try {
      await apiService.deleteApi(api.uuid);
      removeApisFromState([api.uuid]);
      setSingleDeleteApi(null);
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : "Failed to delete API");
    }
  };

  const handleBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setBulkDeleting(true);
    try {
      const results = await Promise.allSettled(
        ids.map((uuid) => apiService.deleteApi(uuid)),
      );
      const failed = results.filter((r) => r.status === "rejected").length;
      const succeeded = ids.filter((_, i) => results[i].status === "fulfilled");
      removeApisFromState(succeeded);
      if (failed > 0) {
        alert(
          `Deleted ${succeeded.length} of ${ids.length} API(s). ${failed} failed.`,
        );
      }
      setBulkDeleteOpen(false);
      if (succeeded.length > 0) await fetchApis();
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : "Failed to delete APIs");
    } finally {
      setBulkDeleting(false);
    }
  };

  const selectedCount = selectedIds.size;

  return (
    <>
      <EntityHubLayout
        hubTitle="API Hub"
        hubDescription="Create, bulk upload, update, and enable or disable API specifications. Filters and edit dialogs are bookmarkable via the URL."
        addPath={pmHubAddPath("api")}
        addLabel="Add API"
        onBulkUpload={() => setBulkOpen(true)}
        loading={loading}
        error={error}
        itemCount={filtered.length}
        hubState={hubState}
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
        pageSizeOptions={HUB_PAGE_SIZE_OPTIONS}
        filterSidebarExtra={
          <TextField
            fullWidth
            size="small"
            label="Version contains"
            value={version}
            onChange={(e) => setVersion(e.target.value)}
            sx={{ mt: 2 }}
          />
        }
      >
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
            {viewMode === "list" && selectedCount > 0 && (
              <>
                <Typography variant="body2" color="text.secondary">
                  {selectedCount} selected
                </Typography>
                <Button
                  size="small"
                  color="error"
                  variant="outlined"
                  startIcon={<DeleteOutlineIcon />}
                  onClick={() => setBulkDeleteOpen(true)}
                  sx={{ textTransform: "none" }}
                >
                  Delete selected
                </Button>
                <Button
                  size="small"
                  onClick={clearSelection}
                  sx={{ textTransform: "none" }}
                >
                  Clear selection
                </Button>
              </>
            )}
          </Box>

          <ToggleButtonGroup
            size="small"
            exclusive
            value={viewMode}
            onChange={(_, value: ViewMode | null) => {
              if (value) {
                setViewMode(value);
                if (value === "card") clearSelection();
              }
            }}
            aria-label="View mode"
          >
            <ToggleButton value="card" aria-label="Card view">
              <GridViewIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton value="list" aria-label="List view">
              <ViewListIcon fontSize="small" />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {viewMode === "list" ? (
          <ApiHubListView
            apis={paginated}
            selectedIds={selectedIds}
            onToggleSelect={toggleSelect}
            onToggleSelectAll={toggleSelectAll}
            allPageSelected={allPageSelected}
            somePageSelected={somePageSelected}
            onEdit={(api) => setEditUuid(api.uuid ?? null)}
            onDelete={(api) => setSingleDeleteApi(api)}
            onToggleEnabled={handleToggle}
            toggleLoadingId={toggleLoadingId}
          />
        ) : (
          <Grid container spacing={2}>
            {paginated.map((api) => (
              <Grid item xs={12} sm={6} md={4} key={api.uuid}>
                <EntityAssetCard
                  title={formatApiDisplayName(api.name)}
                  subtitle={api.description ?? undefined}
                  status={api.status}
                  metadata={
                    <>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Version: {api.version || "—"}
                      </Typography>
                      {api.specContent && (
                        <Chip
                          label="Spec attached"
                          size="small"
                          variant="outlined"
                          sx={{ mt: 0.5 }}
                        />
                      )}
                    </>
                  }
                  enabled={api.status === EntityStatus.ACTIVE}
                  toggleLoading={toggleLoadingId === api.uuid}
                  onToggleEnabled={() => handleToggle(api)}
                  onEdit={() => setEditUuid(api.uuid ?? null)}
                  onDelete={() => api.uuid && handleDeleteOne(api)}
                />
              </Grid>
            ))}
            {!loading && filtered.length === 0 && (
              <Grid item xs={12}>
                <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                  No APIs match your filters.
                </Typography>
              </Grid>
            )}
          </Grid>
        )}
      </EntityHubLayout>

      <BulkUploadApiSpecsDialog
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        onComplete={() => fetchApis()}
      />

      <EditApiDialog
        api={editApi}
        open={Boolean(editUuid && editApi)}
        onClose={() => setEditUuid(null)}
        onSaved={(updated) =>
          setApis((prev) =>
            prev.map((a) => (a.uuid === updated.uuid ? updated : a)),
          )
        }
      />

      <Dialog
        open={bulkDeleteOpen}
        onClose={() => !bulkDeleting && setBulkDeleteOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete selected APIs?</DialogTitle>
        <DialogContent>
          Delete {selectedCount} API{selectedCount === 1 ? "" : "s"}? This cannot
          be undone.
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setBulkDeleteOpen(false)}
            disabled={bulkDeleting}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleBulkDelete}
            disabled={bulkDeleting}
          >
            {bulkDeleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(singleDeleteApi)}
        onClose={() => setSingleDeleteApi(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete API?</DialogTitle>
        <DialogContent>
          Are you sure you want to delete &quot;{singleDeleteApi?.name}&quot;?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSingleDeleteApi(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() =>
              singleDeleteApi && handleDeleteOne(singleDeleteApi)
            }
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ApiHubPage;
